import { useCallback, useEffect, useRef, useState } from 'react'
import { Transition } from 'react-transition-group'

import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import { getContentfulUrl } from '@/utils/contentful'
import { CollectibleEntity } from '@/webgl/runner/entities/CollectibleEntity'

export type RunnerShoppingCartItemProps = {
  item?: CollectibleEntity
  isInCart: boolean
  index: number
}

export default function RunnerShoppingCartItem({ item, isInCart, index }: RunnerShoppingCartItemProps) {
  const { gsap } = useGSAP()
  const elementRef = useRef<HTMLDivElement>(null)

  const onEnter = useCallback(() => {
    const ctx = gsap.context(() => {
      gsap
        .timeline({
          // onComplete: () => setIsVisible(false)
        })
        .fromTo(
          elementRef.current,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.5,
            ease: 'power4.out',
          },
          0
        )
        .fromTo(
          elementRef.current,
          {
            yPercent: 100,
          },
          {
            yPercent: 0,
            duration: 0.6,
            ease: 'back.in(1.2)',
          },
          0
        )
        .fromTo(
          elementRef.current,
          {
            rotate: index % 2 === 0 ? -10 : 10,
          },
          {
            rotate: index % 2 === 0 ? 90 : -90,
            duration: 1,
            ease: 'power1.inOut',
          },
          0
        )
    })

    return () => ctx?.kill()
  }, [index, elementRef, gsap])

  const onExit = useCallback(() => {
    const ctx = gsap.context(() => {
      gsap
        .timeline({})
        .fromTo(
          elementRef.current,
          {
            opacity: 1,
          },
          {
            opacity: 0,
            duration: 0.3,
            ease: 'power4.in',
          },
          0.3
        )
        .fromTo(
          elementRef.current,
          {
            yPercent: 0,
          },
          {
            yPercent: 100,
            duration: 0.6,
            ease: 'back.out(1.7)',
          },
          0
        )
        .fromTo(
          elementRef.current,
          {
            rotate: index % 2 === 0 ? 90 : -90,
          },
          {
            rotate: 0,
            duration: 0.4,
            ease: 'power1.inOut',
          },
          0
        )
    })

    return () => ctx?.kill()
  }, [index, elementRef, gsap])

  const betterFormat = useImageFormatContext()
  const [imageUrl, setImageUrl] = useState(null)
  useEffect(() => {
    const url = getContentfulUrl(`/src/assets/runner/${item.data.id}.png`, { h: 188, fm: betterFormat })
    setImageUrl(url)
  }, [item, betterFormat])

  return (
    <Transition
      appear
      in={isInCart}
      mountOnEnter
      unmountOnExit
      nodeRef={elementRef}
      onEnter={onEnter}
      onExit={onExit}
      timeout={{
        enter: 1000,
        exit: 1000,
      }}
    >
      <div ref={elementRef} className='absolute bottom-0 left-0 w-full h-96'>
        <img src={imageUrl} alt={item?.data.id} className='w-full h-full object-contain' />
      </div>
    </Transition>
  )
}
